<template>
	<ModuleCard ref="module_card" class="width-100" :flowData="flowData" :menuItems="enabledMenuItems" :loading="duplicatingWorkflow" />
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ModuleCard from '@/components/Modules/ModuleCard.vue';
import { ProjectPermissions } from '@ply-io/ply-front-shared-utilities';
import { getTwoWayBindingToStoreMixin } from '@/utils/helpers';
import ErrorHandlingService from '@/utils/error-handling-service';
import { WORKFLOW_ERROR_CODES } from '@/utils/workflow-service.const';
import { projectHasPermission } from '@/store/modules/projects/getters';
import { isConfigurationsOn } from '@/store/modules/featureFlags/getters';
import { selectWorkflowId } from '@/store/modules/selectedWorkflow/actions';
import { selectedProjectId } from '@/store/modules/selectedProject/getters';
import { toggleWorkflowIsOnSettings } from '@/store/modules/workflows/actions';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import AppDialogManagerService from '../dialogs/AppDialogManager/app-dialog-manager-service';
import WorkflowConfigurationResponseService from '@/utils/workflow-configuration-response-service';
import { APP_CONFIRMATION_DIALOG_RESPONSES } from '../dialogs/AppDialogs/app-confirmation-dialog.const';
import { updateWorkflow, deleteWorkflow, duplicateWorkflow, moveWorkflowToOtherProject } from '@/store/modules/workflows/actions';
import {
	hasWorkflowEditPermissions,
	hasWorkflowDeletePermissions,
	hasWorkflowEditGeneralConfigurationPermissions,
} from '@/store/modules/workflows/getters';

const twoWayBindingToStoreMixin = getTwoWayBindingToStoreMixin('flowData', 'saveFlow', {
	flowTitle: 'workflow_title',
});

export default {
	name: 'FlowsViewModuleCard',
	components: { ModuleCard },
	mixins: [twoWayBindingToStoreMixin],
	props: { flowData: { type: Object } },
	data() {
		return { duplicatingWorkflow: false };
	},
	computed: {
		...mapGettersShorthand({ selectedProjectId }),
		...mapGetters([
			hasWorkflowEditPermissions.fullPath,
			hasWorkflowDeletePermissions.fullPath,
			hasWorkflowEditGeneralConfigurationPermissions.fullPath,
			isConfigurationsOn.fullPath,
			projectHasPermission.fullPath,
		]),

		menuItems() {
			const items = [
				{ text: 'Rename', onClick: this.renameWorkflow, disabled: !this.hasEditPermissions },
				{ text: 'Edit', onClick: this.editModule, disabled: !this.editFlowEnabled },
				{ text: 'Move to...', onClick: this.moveToOtherProject, disabled: !this.editFlowEnabled || !this.hasMovePermissions },
				{ divider: true, disabled: !this.editFlowEnabled }, // show only if all edit options are on
			];
			if (this[isConfigurationsOn.fullPath] && (this.hasUserSetup || this.hasGeneralSetup)) {
				if (this.hasUserSetup || this.hasGeneralSetup) {
					items.push(...[{ text: 'Edit Configuration', onClick: this.editConfiguration, disabled: !this.showEditConfiguration }]);
				}
				items.push(...[{ divider: true, disabled: !this.editFlowEnabled }]); // if we dont have edit permissions, we dont need to divide this section
			}
			items.push({
				text: 'Duplicate draft version',
				onClick: () => this.openDuplicateWorkflowDialog(false),
				disabled: !this.editFlowEnabled,
			});
			if (this.flowData?.is_published) {
				items.push(
					...[
						{
							text: 'Duplicate published version',
							onClick: () => this.openDuplicateWorkflowDialog(true),
							disabled: !this.editFlowEnabled,
						},
					]
				);
			}
			items.push({ divider: true, disabled: !this.editFlowEnabled });
			items.push(...[{ text: 'Delete', onClick: this.openDeleteModuleDialog, disabled: !this.hasDeletePermission }]);
			return items;
		},

		installedFromInstallable() {
			return !!this.flowData.installed_from;
		},

		hasMovePermissions() {
			return this[projectHasPermission.fullPath](this.flowData.project, ProjectPermissions.ChangeSettings);
		},
		moduleHasInstallable() {
			return !!this.flowData.installable;
		},
		hasEditPermissions() {
			return this[hasWorkflowEditPermissions.fullPath](this.flowData.id);
		},
		hasEditGeneralConfigurationPermissions() {
			return this[hasWorkflowEditGeneralConfigurationPermissions.fullPath](this.flowData.id);
		},

		hasDeletePermission() {
			return this[hasWorkflowDeletePermissions.fullPath](this.flowData.id);
		},
		editFlowEnabled() {
			// has permission and is not installed from somewhere
			return this.hasEditPermissions && !this.installedFromInstallable;
		},
		showEditConfiguration() {
			return this.hasUserSetup || (this.hasGeneralSetup && this.hasEditGeneralConfigurationPermissions);
		},
		enabledMenuItems() {
			return this.menuItems.filter((x) => !x.disabled);
		},
		moduleEditRouterTo() {
			return { name: 'Feature', params: { pID: this.flowData.project, wfID: this.flowData.id } };
		},
		userSetupId() {
			return this.flowData.current_version_user_setup;
		},
		generalSetupId() {
			return this.flowData.current_version_general_setup;
		},
		hasUserSetup() {
			return !!this.userSetupId;
		},
		hasGeneralSetup() {
			return !!this.generalSetupId;
		},
	},
	methods: {
		...mapActionsShorthand({ duplicateWorkflow, toggleWorkflowIsOnSettings }),
		...mapActions([updateWorkflow.fullPath, deleteWorkflow.fullPath, selectWorkflowId.fullPath, moveWorkflowToOtherProject.fullPath]),
		async openDeleteModuleDialog() {
			if (this.moduleHasInstallable) {
				const dialogProps = {
					title: 'Delete feature',
					noButtonText: 'Cancel',
					yesButtonText: 'Delete feature',
					onYes: () => this[deleteWorkflow.fullPath]({ id: this.flowData.id }),
					areYouSureText: `Are you sure you want to delete the feature <h3 style="color: #f55366">&nbsp;${this.flowTitle}</h3> ?`,
					verificationDescription: 'Please type the name of the feature to confirm.',
					confirmationText: this.flowTitle,
					dangerText: 'IMPORTANT - This feature has an installable related to. Are you sure you want to delete this feature?',
				};
				await AppDialogManagerService.openDangerConfirmationDialog(dialogProps);
			} else {
				const res = await AppDialogManagerService.openDeleteFeatureDialog();
				if (res === APP_CONFIRMATION_DIALOG_RESPONSES.YES) {
					// Todo: Create a PlyPromise protocol for async results (=== 'yes' is not acceptable)
					try {
						await this[deleteWorkflow.fullPath]({ id: this.flowData.id });
					} catch (error) {
						this.$toast.error(ErrorHandlingService.extractErrorMessage(error));
					}
				}
			}
		},
		async openDuplicateWorkflowDialog(duplicatePublishedVersion) {
			const dialogProps = {
				title: `Are you sure you want to duplicate the ${duplicatePublishedVersion ? 'published' : 'draft'} version of this feature?`,
				description: 'It might take a minute to complete this action',
				yesText: 'Duplicate',
				noText: 'Cancel',
				isDanger: false,
			};
			const res = await AppDialogManagerService.openConfirmationDialog(dialogProps);
			if (res === APP_CONFIRMATION_DIALOG_RESPONSES.YES) {
				this.duplicatingWorkflow = true;
				try {
					const duplicatedWorkflow = await this.duplicateWorkflow({
						id: this.flowData.id,
						versionId: duplicatePublishedVersion ? this.flowData.current_version : this.flowData.current_draft.id,
					});
					this.$emit('newWorkflow', duplicatedWorkflow.id);
				} finally {
					this.duplicatingWorkflow = false;
				}
			}
		},

		async saveFlow(data) {
			await this[updateWorkflow.fullPath]({ id: this.flowData.id, data }); // directly call store's updateWorkflow to be synced with server
		},

		renameWorkflow() {
			this.$refs.module_card.renameWorkflow();
		},

		editModule() {
			if (this.editFlowEnabled) {
				this.$router.push(this.moduleEditRouterTo);
			}
		},
		async moveToOtherProject() {
			try {
				const selectedProjectId = await AppDialogManagerService.openMoveProjectDialog({
					preSelectedProject: this.flowData.project,
				});
				if (selectedProjectId) {
					await this[moveWorkflowToOtherProject.fullPath]({
						workflowId: this.flowData.id,
						oldProjectId: this.flowData.project,
						projectId: selectedProjectId,
					});
					this.$toast.success('Feature successfully moved');
				}
			} catch (err) {
				if (
					ErrorHandlingService.extractErrorCode(err) === WORKFLOW_ERROR_CODES.MOVE_WORKFLOW_FAILED &&
					!!ErrorHandlingService.extractErrorMessage(err)
				) {
					this.$toast.error(ErrorHandlingService.extractErrorMessage(err));
				} else {
					this.$toast.error('Failed moving to different project');
				}
			}
		},
		editConfiguration() {
			this[selectWorkflowId.fullPath]({ id: this.flowData.id });
			return WorkflowConfigurationResponseService.editAndSaveConfiguration(
				{
					generalConfigurationId: this.hasEditGeneralConfigurationPermissions ? this.generalSetupId : null,
					userSpecificConfigurationId: this.userSetupId,
				},
				false
			);
		},
	},
};
</script>
